import { useQuery } from "@tanstack/react-query";
import apiClient from "../api/apiClient";

interface RolePermission {
  id: number;
  role_id: number;
  permission_id: number;
  status: number;
  created_at: string;
  updated_at: string;
  deleted_at: null | string;
}

interface Role {
  id: number;
  name: string;
  description: string;
  total_users?: number;
  permissions: RolePermission[];
}

// Define the return type for clarity
interface UserPermissionsData {
  userRole: {
    id: number;
    user_id: number;
    role_id: number;
    created_at: string;
    updated_at: string;
    deleted_at: null | string;
  };
  role: Role;
  permissions: Record<number, boolean>;
}

/**
 * Hook to fetch user permissions directly from the API
 * This allows us to get fresh permissions without requiring a user logout
 */
export const useUserPermissions = (userId?: number) => {
  return useQuery<UserPermissionsData | null, Error>({
    queryKey: ["userPermissions", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }

      try {
        // First get the user role with correct response structure
        const userRoleResponse = await apiClient.get(
          `/userrole?user_id=${userId}`
        );

        // Check if we have data in the response
        if (
          !userRoleResponse.data.data ||
          !Array.isArray(userRoleResponse.data.data) ||
          userRoleResponse.data.data.length === 0
        ) {
          throw new Error("User role not found");
        }

        // Access the first user role from the array
        const userRole = userRoleResponse.data.data[0];

        if (!userRole || !userRole.role_id) {
          throw new Error("Invalid user role data");
        }

        // Then get the complete role with permissions
        const roleResponse = await apiClient.get<{
          status: boolean;
          data: Role;
        }>(`/role/${userRole.role_id}`);

        if (!roleResponse.data.data) {
          throw new Error("Role information not found");
        }

        // Process permissions to get active permissions map
        const permissions = processPermissions(roleResponse.data.data);

        return {
          userRole,
          role: roleResponse.data.data,
          permissions,
        };
      } catch (error) {
        console.error("Error fetching user permissions:", error);
        throw error;
      }
    },
    // Enable query only if we have a userId
    enabled: !!userId,
    // Refetch every 5 minutes
    refetchInterval: 1000 * 60 * 5,
    // Continue to show stale data while revalidating
    staleTime: 1000 * 60 * 4.5, // Set stale time slightly less than refetch interval
  });
};

// Process permissions to get active permissions map
const processPermissions = (role: Role): Record<number, boolean> => {
  const permissionsMap: Record<number, { status: boolean; updatedAt: string }> =
    {};

  // Process permissions to get the latest status for each
  role.permissions.forEach((permission) => {
    const permId = permission.permission_id;

    // If we haven't seen this permission yet, or this one is newer, update the map
    if (
      !permissionsMap[permId] ||
      new Date(permission.updated_at) >
        new Date(permissionsMap[permId].updatedAt)
    ) {
      permissionsMap[permId] = {
        status: permission.status === 1,
        updatedAt: permission.updated_at,
      };
    }
  });

  // Convert to simple permission_id -> boolean map
  const result: Record<number, boolean> = {};
  Object.entries(permissionsMap).forEach(([permId, data]) => {
    result[Number(permId)] = data.status;
  });

  return result;
};

// Helper functions to check permissions
export const hasPermission = (
  permissions: Record<number, boolean> | undefined,
  permissionId: number
): boolean => {
  if (!permissions) return false;
  return permissions[permissionId] === true;
};

export const hasAnyPermission = (
  permissions: Record<number, boolean> | undefined,
  permissionIds: number[]
): boolean => {
  if (!permissions) return false;
  return permissionIds.some((id) => permissions[id] === true);
};

export const hasAllPermissions = (
  permissions: Record<number, boolean> | undefined,
  permissionIds: number[]
): boolean => {
  if (!permissions) return false;
  return permissionIds.every((id) => permissions[id] === true);
};
